import {WS} from '../../utils/ws';
import {RPCMessage} from './types';

type Executor<T> = [(value?: T | PromiseLike<T>) => void, (reason?: any) => void];

export class RPCService<K> {

    private sequence = 0;
    private waitingList = new Map<number, Executor<any>>();

    public constructor(private client: K, private websocket: WS) {
        this.websocket.onmessage = data => {
            this.handleMessage(JSON.parse(data.data));
        };
    }

    protected ask<T>(method: string, params?: any): Promise<T> {
        this.sequence += 1;
        const sequence = this.sequence;

        if (params == null) {
            // @ts-ignore
            params = {};
        }

        const message: RPCMessage<any> = {
            is_request: true,
            id: sequence,
            params: [params],
            method,
        };

        return new Promise<T>((resolve, reject) => {
            this.waitingList.set(sequence, [resolve, reject]);
            this.websocketSend(message);
        });
    }

    protected handleMessage<T>(message: RPCMessage<T>) {
        if (message.is_request) {
            this.answerRequest(message);
        } else {
            this.applyResponse(message);
        }
    }

    private answerRequest<T, K>(message: RPCMessage<T>) {
        const method = this.toCamelCase(message.method);
        const response: RPCMessage<K> = {
            id: message.id,
            is_request: false,
            params: null,
            method: message.method,
        };

        try {
            response.params = [this.client[method].apply(this.client, message.params) as K];
        } catch (e) {
            response.error = e.toLocaleString();
        }

        this.websocketSend(response);
    }

    private applyResponse<T>(message: RPCMessage<T>) {
        const executor = this.waitingList.get(message.id);
        if (executor) {
            this.waitingList.delete(message.id);
            const [resolve, reject] = executor;
            if (message.error) {
                reject(message.error);
            } else {
                if (message.params && message.params.length) {
                    resolve(message.params[0]);
                } else {
                    resolve(null);
                }
            }
        }
    }

    private websocketSend<T>(message: RPCMessage<T>) {
        this.websocket.send(JSON.stringify(message));
    }

    private toCamelCase(input: string): string {
        const firstCharacter = input[0];
        return this.replaceAt(input, 0, firstCharacter.toLowerCase());
    }

    private replaceAt = (input: string, index: number, replacement: string): string => {
        return input.substr(0, index) + replacement + input.substr(index + replacement.length);
    };
}
